import React from 'react'
import { AttendanceConversationData } from './conversation'
import { MessageInfoLauncher } from '../../modals/messageinfo/messageInfo'
import { Badge, BadgeStyle } from '../../../components/simple/badge'
import { IconAttendance } from '../../../icons/library'
import { MiniPie } from '../../../components/charts/miniPie'
import { TextInput } from '../../../components/form/textInput'
import { Checkbox, LabeledCheckbox } from '../../../components/form/checkbox'
import { LocalTable } from '../../../components/table'
import { Link } from 'react-router-dom'
import { makeRequest, showAxiosErrorAsToast } from '../../../request'
import { usePusher } from '../../../providers/pusher'
import { getComposeLink } from '../../../providers/URLParams'

export const AttendanceConversation: React.FC<{ data: AttendanceConversationData }> = ({ data: initialData }) => {
  const [data, setData] = React.useState(initialData)
  const [showResolved, setShowResolved] = React.useState(true)
  const [showUnexcused, setShowUnexcused] = React.useState(true)
  const [search, setSearch] = React.useState('')

  const modifyResolved = (id: number, resolved: boolean) => {
    const existing = data.absences.find((absence) => absence.id === id)
    if (!existing || existing.resolved === resolved) return
    setData({
      ...data,
      absences: data.absences.map((absence) => (absence.id === id ? { ...absence, resolved } : absence)),
    })
  }

  usePusher<{ id: number; resolved: boolean }>(data.pusherChannel, 'rmt', (change) => {
    modifyResolved(change.id, change.resolved)
  })

  const setResolved = (absenceID: number, resolved: boolean) => {
    modifyResolved(absenceID, resolved)
    makeRequest('/attendance/resolve', { id: absenceID, resolved: resolved }).catch(showAxiosErrorAsToast)
  }

  return (
    <>
      <MessageInfoLauncher
        messageID={data.id}
        className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-200 p-4"
        canLaunch={data.blocks.length === 0}
      >
        <div className="flex items-center gap-2 overflow-hidden text-nowrap -sm:ml-6">
          Attendance for <b>{data.schoolName}</b> on <b>{data.date}</b>
        </div>
        <div className="flex items-center gap-2">
          {data.blocks.map((block, index) => (
            <MessageInfoLauncher messageID={block.messageID} key={index} canLaunch>
              <Badge badgeStyle={BadgeStyle.logo}>block {block.name}</Badge>
            </MessageInfoLauncher>
          ))}
          <IconAttendance size="lg" />
        </div>
      </MessageInfoLauncher>
      <div className="m-2 flex flex-col gap-1">
        <div className="mt-2 flex items-center gap-3">
          <MiniPie size={32} data={data.attendancePie} />
          <b>{data.totalAbsences} students absent</b>
        </div>
        <div className="ml-2 flex items-center gap-2">
          {data.replyable && (
            <LabeledCheckbox checked={showResolved} onChecked={setShowResolved} label="Show Resolved" />
          )}
          <LabeledCheckbox checked={showUnexcused} onChecked={setShowUnexcused} label="Show Unexcused" />
          <TextInput value={search} onChange={setSearch} placeholder="Search" className="ml-auto" />
        </div>
        <LocalTable
          exportName={`Attendance for ${data.schoolName} on ${data.date}`}
          pagination={10}
          saveLocationOnDataChange={true}
          search={search}
          data={data.absences.filter(
            (absence) =>
              (showResolved || !absence.resolved) &&
              (showUnexcused || absence.excusedThroughSIS || absence.repliedByGuardian)
          )}
          columns={[
            {
              id: 'student',
              header: 'Student',
              cell: ({ row }) => (
                <Link
                  className="!text-neutral-900 no-underline hover:underline"
                  to={getComposeLink({ students: row.original.student.id })}
                >
                  {row.original.student.name}
                </Link>
              ),
              accessorKey: 'student.name',
            },
            {
              id: 'class',
              header: 'Class',
              cell: ({ row }) => (
                <Badge
                  badgeStyle={BadgeStyle.logo}
                  tooltip={
                    <>
                      {row.original.student.name} was absent from <b>{row.original.blocks.join(', ')}</b>
                    </>
                  }
                >
                  {row.original.blocks.length}
                </Badge>
              ),
              accessorKey: 'blocks.length',
              show: data.attendancePerClass,
            },
            {
              id: 'response',
              header: 'Response',
              cell: ({ row }) => (
                <>
                  {row.original.repliedByGuardian ? (
                    <>
                      {row.original.repliedByGuardian.comment}{' '}
                      <Link
                        className="!text-neutral-900 no-underline hover:underline"
                        to={getComposeLink({
                          students: row.original.student.id,
                          individuals: row.original.repliedByGuardian.id,
                        })}
                      >
                        ({row.original.repliedByGuardian.name})
                      </Link>{' '}
                      {row.original.repliedByGuardian.attachmentURL && (
                        <a
                          href={row.original.repliedByGuardian.attachmentURL}
                          target="_blank"
                          rel="noreferrer noopener"
                        >
                          Attachment
                        </a>
                      )}
                    </>
                  ) : (
                    row.original.excusedThroughSIS && <>Excused Through SIS</>
                  )}
                </>
              ),
              accessorFn: (row) =>
                row.repliedByGuardian
                  ? `${row.repliedByGuardian.comment} (${row.repliedByGuardian.name})`
                  : row.excusedThroughSIS
                    ? 'Excused Through SIS'
                    : '',
              show: data.replyable,
            },
            {
              id: 'resolved',
              header: 'Resolved',
              cell: ({ row }) =>
                row.original.repliedByGuardian ? (
                  <Checkbox
                    checked={row.original.resolved}
                    onChecked={() => setResolved(row.original.id, !row.original.resolved)}
                  />
                ) : row.original.excusedThroughSIS ? (
                  <Checkbox checked={true} disabled />
                ) : (
                  <></>
                ),
              accessorFn: (row) => row.resolved,
              sortingFn: (a, b) =>
                (a.original.resolved ? -1 : 1) +
                (a.original.repliedByGuardian ? -10 : 10) -
                (b.original.resolved ? -1 : 1) -
                (b.original.repliedByGuardian ? -10 : 10),
              show: data.replyable,
            },
            {
              id: 'excused',
              header: 'Excused',
              cell: ({ row }) => row.original.excusedThroughSIS && <>Excused Through SIS</>,
              accessorFn: (row) => row.excusedThroughSIS,
              show: !data.replyable,
            },
          ]}
        />
      </div>
    </>
  )
}
